import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LayoutGrid, GitBranch, HardDrive, Gauge, Key } from "lucide-react";
import { formatBytes } from "@/lib/formatters";
import type { BillingStatus } from "../api/billing.api";

interface QuotasGridProps {
  quotas: BillingStatus["quotas"];
  /** Optional heading override */
  title?: string;
}

/**
 * Grid displaying quota limits for the current plan.
 * Used in both free tier and paid tier views.
 */
export function QuotasGrid({ quotas, title }: QuotasGridProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
      <h3 className="text-base font-semibold text-heading">{title ?? t("billing.quotas")}</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <QuotaItem
          icon={<LayoutGrid size={16} className="shrink-0 text-muted" />}
          label={t("billing.maxApps")}
          value={quotas.max_apps.toString()}
        />
        <QuotaItem
          icon={<GitBranch size={16} className="shrink-0 text-muted" />}
          label={t("billing.maxDeploymentsPerApp")}
          value={quotas.max_deployments_per_app.toString()}
        />
        <QuotaItem
          icon={<HardDrive size={16} className="shrink-0 text-muted" />}
          label={t("billing.maxStorage")}
          value={formatBytes(quotas.max_storage_bytes)}
        />
        <QuotaItem
          icon={<Gauge size={16} className="shrink-0 text-muted" />}
          label={t("billing.apiRateLimit")}
          value={`${quotas.api_rate_limit_per_minute}${t("billing.perMinute")}`}
        />
        <QuotaItem
          icon={<Key size={16} className="shrink-0 text-muted" />}
          label={t("billing.maxApiKeys")}
          value={quotas.max_api_keys.toString()}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuotaItem (internal)
// ---------------------------------------------------------------------------

function QuotaItem({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-elevated/50 p-3 border border-border/30">
      {icon}
      <div>
        <p className="text-xs text-muted font-medium">{label}</p>
        <p className="text-sm font-bold text-heading mt-0.5">{value}</p>
      </div>
    </div>
  );
}
